import { useEffect, useState } from "react";
import { MainLayout } from "../../layout/MainLayout";
import DataTable, { TableColumn } from "react-data-table-component";
import { Org, emptyOrg } from "../../interfaces/Org";
import OrgAdd from "./OrgAdd";
import OrgDelete from "./OrgDelete";
import OrgEdit from "./OrgEdit";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LoadingSpinner } from "../../elements/LoadingSpinner";
import { Form, InputGroup, Button } from "react-bootstrap";
import { handleApiError, safeJsonParse } from "../../../utils/api";

export default function Orgs() {
    const { t } = useTranslation();

    const navigate = useNavigate();
    const isAdmin = localStorage.getItem('isAdmin');

    const [orgs, setOrgs] = useState<Org[]>([]);
    const [selectedOrg, setSelectedOrg] = useState<Org>(emptyOrg);
    const [showAddForm, setShowAddForm] = useState(false);
    const [showDeleteForm, setShowDeleteForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [loading, setLoading] = useState(true);
    const [reloadData, setReloadData] = useState(0);

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const columns: TableColumn<Org>[] = [
        {
            name: t("Id"),
            selector: (row: Org) => row._id,
            sortable: true,
            width: '200px',
            omit: true,
        },
        {
            name: t("Title"),
            selector: (row: Org) => row.name,
            sortable: true,
            grow: 2,
        },
        {
            name: t("Actions"),
            cell: (row: Org) => (
                <div className="actions">
                    <a
                        className="edit-link"
                        title={t("Edit organization")}
                        href="#"
                        onClick={(event) => handleShowEditForm(event, row._id)}
                    ></a>
                    <a
                        className="delete-link"
                        title={t("Delete organization")}
                        href="#"
                        onClick={(event) => handleShowDeleteForm(event, row._id)}
                    ></a>
                </div>
            ),
            width: '150px',
        },
    ];

    const ExpandedComponent = ({ data }: { data: Org }) => (
        <div style={{ padding: '15px' }}>
            <div><strong>{t("Id")}:</strong> {data._id}</div>
        </div>
    );

    useEffect(() => {
        setLoading(true);
        const requestOptionsPost = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            }
        };
        fetch(import.meta.env.VITE_API_URL + '/v1/org', requestOptionsPost)
            .then(response => {
                if (handleApiError(response, navigate)) {
                    return null;
                }
                return safeJsonParse(response);
            })
            .then(data => {
                if (!data) return;
                setOrgs(data);
                setLoading(false);
            });
    }, [reloadData]);

    const handleShowAddForm = () => {
        setShowAddForm(true);
    }

    const handleHideAddForm = () => {
        setShowAddForm(false);
    }

    const handleSumbitAddForm = (f: Org) => {
        setShowAddForm(false);
        setReloadData(prev => prev + 1);
    }
    
    const handleShowDeleteForm = (event: any, id: string) => {
        event.preventDefault()
        const newSelectedOrg: Org = orgs.find((o: Org) => o._id == id) ?? emptyOrg;
        if (newSelectedOrg._id != '') {
            setSelectedOrg(newSelectedOrg);
            setShowDeleteForm(true);
        } else {
            alert(t("No organization selected."));
        }
    }
    
    const handleHideDeleteForm = () => {
        setShowDeleteForm(false);
    }

    const handleSumbitDeleteForm = (id: string) => {
        setShowDeleteForm(false);
        setReloadData(prev => prev + 1);
    }
    
    const handleShowEditForm = (event: any, id: string) => {
        event.preventDefault()
        const newSelectedOrg: Org = orgs.find((o: Org) => o._id == id) ?? emptyOrg;
        if (newSelectedOrg._id != '') {
            setSelectedOrg(newSelectedOrg);
            setShowEditForm(true);
        } else {
            alert(t("No organization selected."));
        }
    }
    
    const handleHideEditForm = () => {
        setShowEditForm(false);
    }

    const handleSumbitEditForm = (org: Org) => {
        setShowEditForm(false);
        setReloadData(prev => prev + 1);
    }

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    }

    const handleClearSearch = () => {
        setSearchQuery('');
    }

    const handleToggleFilters = () => {
        setShowFilters(!showFilters);
    }

    // Filter organizations based on search query
    const filteredOrgs = orgs.filter(org => {
        if (!searchQuery.trim()) return true;

        const searchLower = searchQuery.toLowerCase();
        return (
            (org.name && org.name.toLowerCase().includes(searchLower)) ||
            (org._id && org._id.toLowerCase().includes(searchLower))
        );
    });

    return (
        <MainLayout>
            <div className="container-fluid">
                <div className="row">
                    { isAdmin &&
                        <div className="admin-orgs-container">
                            <div className="page-header">
                                <h1>{t("Organizations management")}</h1>
                                <div className="page-actions">
                                    <button
                                        className={`filter-toggle ${showFilters ? 'active' : 'inactive'}`}
                                        onClick={handleToggleFilters}
                                        title={t("Filters")}
                                    >
                                        <i className="bi bi-funnel"></i>
                                    </button>
                                    <a className="action-icon" onClick={handleShowAddForm} title={t("New organization")}>
                                        <i className="bi bi-plus-circle"></i>
                                    </a>
                                </div>
                            </div>

                            {/* Search controls */}
                            <div className={`org-filters ${!showFilters ? 'hidden' : ''}`}>
                                <div className="filter-controls">
                                    <Form.Group className="search-group">
                                        <Form.Label>{ t("Search") }</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="text"
                                                placeholder={ t("Search by organization name...") }
                                                value={searchQuery}
                                                onChange={handleSearchChange}
                                            />
                                            {searchQuery && (
                                                <Button variant="secondary" onClick={handleClearSearch}>
                                                    { t("Clear") }
                                                </Button>
                                            )}
                                        </InputGroup>
                                    </Form.Group>
                                </div>

                                <div className="filter-info">
                                    { t("Total results") }: <strong>{filteredOrgs.length}</strong> / {orgs.length}
                                    {searchQuery && (
                                        <span className="search-term">
                                            { t("Searching for") }: <strong>"{searchQuery}"</strong>
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="admin-orgs-table-container">
                                <DataTable
                                    columns={columns}
                                    data={filteredOrgs}
                                    progressPending={loading}
                                    progressComponent={<LoadingSpinner />}
                                    pagination
                                    paginationPerPage={10}
                                    paginationRowsPerPageOptions={[10, 20, 30, 50]}
                                    paginationComponentOptions={{
                                        rowsPerPageText: t('Rows per page') + ':',
                                        rangeSeparatorText: t('of'),
                                    }}
                                    highlightOnHover
                                    striped
                                    responsive
                                    noDataComponent={t("No organizations found")}
                                    expandableRows
                                    expandableRowsComponent={ExpandedComponent}
                                />
                            </div>
                        </div>
                    }
                    { !isAdmin &&
                        <div className="admin-orgs-container">
                            { t("You are not allowed to access this section.") }
                        </div>
                    }
                </div>
            </div>
            { showAddForm &&
                <OrgAdd submitCallback={ handleSumbitAddForm } closeCallback={ handleHideAddForm } />
            }
            { showDeleteForm &&
                <OrgDelete org={ selectedOrg } submitCallback={ handleSumbitDeleteForm } closeCallback={ handleHideDeleteForm } />
            }
            { showEditForm &&
                <OrgEdit org={ selectedOrg } submitCallback={ handleSumbitEditForm } closeCallback={ handleHideEditForm } />
            }
        </MainLayout>);
}